import { Type } from "@sinclair/typebox";
import { IdSchema, JsonValueSchema, Sha256Schema, TimestampSchema } from "./shared.js";
import { ReportPlanSchema } from "./report.js";
import { ResearchRequirementDefinitionSchema, ResearchRequirementSchema } from "./research.js";
export * from "./research.js";
export const ModelRoleSchema = Type.Union([
    Type.Literal("planner"),
    Type.Literal("worker"),
    Type.Literal("vision"),
]);
export const ModelImageReferenceSchema = Type.Object({
    path: Type.String({ pattern: "^(input|result)/[^\\u0000]+$", maxLength: 4096 }),
    digest: Sha256Schema,
    sizeBytes: Type.Integer({ minimum: 1, maximum: 32 * 1024 * 1024 }),
    mediaType: Type.Union([
        Type.Literal("image/png"),
        Type.Literal("image/jpeg"),
        Type.Literal("image/webp"),
    ]),
}, { additionalProperties: false });
/** 图片分批与签名绑定共用预算；每张图片仍独立核验摘要、大小和授权。 */
export const MODEL_IMAGE_BATCH_LIMIT = 8;
export const ModelCallBindingSchema = Type.Object({
    purpose: Type.Optional(Type.Union([Type.Literal("research"), Type.Literal("report"), Type.Literal("guide_review")])),
    budgetOwnerTurnId: Type.Optional(IdSchema),
    guideReviewId: Type.Optional(IdSchema),
    guideReviewLeaseOwner: Type.Optional(IdSchema),
    guideReviewLeaseFence: Type.Optional(Type.String({ pattern: "^[1-9][0-9]{0,18}$" })),
    guideReviewSnapshotDigest: Type.Optional(Sha256Schema),
    guideReviewChunkDigest: Type.Optional(Sha256Schema),
    requestId: IdSchema,
    identityId: IdSchema,
    sessionId: IdSchema,
    turnId: IdSchema,
    generation: Type.Integer({ minimum: 0 }),
    targetDigest: Sha256Schema,
    seriesConfigDigest: Sha256Schema,
    deploymentConfigDigest: Sha256Schema,
    role: ModelRoleSchema,
    modelName: Type.String({ minLength: 1, maxLength: 255 }),
    prompt: Type.String({ minLength: 1 }),
    inputPrefix: Type.String(),
    phase: Type.Union([
        Type.Literal("task"),
        Type.Literal("arguments"),
        Type.Literal("title"),
        Type.Literal("report"),
    ]),
    instructions: Type.String({ minLength: 1 }),
    outputSchema: JsonValueSchema,
    images: Type.Array(ModelImageReferenceSchema, { maxItems: MODEL_IMAGE_BATCH_LIMIT }),
    codex: Type.Optional(Type.Object({
        contextKey: Sha256Schema,
        catalogDigest: Sha256Schema,
        contextWindow: Type.Integer({ minimum: 1, maximum: 10_000_000 }),
        autoCompactTokenLimit: Type.Integer({ minimum: 1, maximum: 10_000_000 }),
    }, { additionalProperties: false })),
    maxOutputTokens: Type.Integer({ minimum: 1 }),
    policyDigest: Sha256Schema,
    inputTokenBound: Type.Optional(Type.Integer({ minimum: 1 })),
    deadline: TimestampSchema,
}, { additionalProperties: false });
export const CollaborationStepSchema = Type.Object({
    title: Type.String({ minLength: 1, maxLength: 500 }),
    acceptanceCriteria: Type.String({ minLength: 1, maxLength: 4000 }),
    dependsOn: Type.Array(Type.Integer({ minimum: 0, maximum: 31 }), {
        maxItems: 31,
        uniqueItems: true,
    }),
    status: Type.Union([
        Type.Literal("pending"),
        Type.Literal("in_progress"),
        Type.Literal("completed"),
    ]),
    reworks: Type.Integer({ minimum: 0, maximum: 9 }),
}, { additionalProperties: false });
export const CollaborationStateSchema = Type.Object({
    requirementDefinitions: Type.Optional(Type.Array(ResearchRequirementDefinitionSchema, { minItems: 1, maxItems: 64 })),
    reportPlan: Type.Optional(ReportPlanSchema),
    requirements: Type.Optional(Type.Array(ResearchRequirementSchema, { minItems: 1, maxItems: 64 })),
    revision: Type.Integer({ minimum: 0 }),
    seriesConfigDigest: Sha256Schema,
    phase: Type.Union([
        Type.Literal("planning"),
        Type.Literal("working"),
        Type.Literal("reviewing"),
        Type.Literal("paused"),
        Type.Literal("completed"),
    ]),
    steps: Type.Array(CollaborationStepSchema, { maxItems: 32 }),
    activeStep: Type.Integer({ minimum: 0, maximum: 32 }),
    replans: Type.Integer({ minimum: 0, maximum: 9 }),
    reviewedObservations: Type.Integer({ minimum: 0 }),
    feedback: Type.String({ maxLength: 12000 }),
    vision: Type.Array(Type.Object({
        image: ModelImageReferenceSchema,
        question: Type.String({ maxLength: 4000 }),
        result: Type.String({ maxLength: 16000 }),
        modelConfigDigest: Sha256Schema,
    }, { additionalProperties: false }), { maxItems: 32 }),
}, { additionalProperties: false });
export const ModelActivitySchema = Type.Object({
    requestId: IdSchema,
    role: ModelRoleSchema,
    modelDisplayName: Type.String({ minLength: 1, maxLength: 255 }),
    modelConfigDigest: Sha256Schema,
    seriesConfigDigest: Sha256Schema,
    stage: Type.Union([
        Type.Literal("started"),
        Type.Literal("completed"),
        Type.Literal("failed"),
        Type.Literal("review"),
        Type.Literal("vision_fallback"),
        Type.Literal("paused"),
    ]),
    reason: Type.String({ maxLength: 12000 }),
    inputTokens: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    outputTokens: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
}, { additionalProperties: false });
//# sourceMappingURL=model-collaboration.js.map